import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { useDispatchApp } from "../../../lib/redux";
import { startGetCreativeTemplate } from "../../../actions/creativeTemplates";
import {
  startListenerMergedFonts,
  stopListenerMergedFonts,
} from "../../../actions/fonts";
import { loadFontFaces } from "../utils/fonts";
import { getOriginColor } from "../utils/layerTree";
import { buildTemplateLayoutPreview } from "../utils/templateLayoutPreview";
import { CroppedLayerImage } from "./CroppedLayerImage";
import { LayerTextPreview } from "./TemplateCanvasOverlay";

const docCache = new Map();
let fontConsumers = 0;
let fontListenerRefs = null;

const getDocCacheKey = ({ templateID, version }) =>
  `${templateID}:${version || 0}`;

const loadTemplateDoc = ({ dispatch, templateID, version }) => {
  const key = getDocCacheKey({ templateID, version });
  if (!docCache.has(key)) {
    docCache.set(
      key,
      dispatch(startGetCreativeTemplate({ templateID })).then((result) =>
        result?.ok ? result.data?.doc || null : null
      )
    );
  }
  return docCache.get(key);
};

const useTemplateFonts = ({ enabled }) => {
  const dispatch = useDispatchApp();
  useEffect(() => {
    if (!enabled) return undefined;
    fontConsumers += 1;
    if (fontConsumers === 1) {
      dispatch(
        startListenerMergedFonts({
          onUpdatedDataCallback: (fonts) => loadFontFaces({ fonts }),
          onReferenceAvailable: (refs) => {
            fontListenerRefs = refs;
          },
        })
      );
    }
    return () => {
      fontConsumers -= 1;
      if (fontConsumers === 0) {
        dispatch(stopListenerMergedFonts({ listenerRefs: fontListenerRefs }));
        fontListenerRefs = null;
      }
    };
  }, [dispatch, enabled]);
};

const objectFitFromBox = (fit) => (fit === "cover" ? "cover" : "fill");

const hasPaintedContent = (box) =>
  Boolean(box.shape) ||
  Boolean(box.assetUrl) ||
  (box.type === "text" && Boolean(String(box.text || "").trim()));

const PreviewBox = ({ box, width, height, scale }) => (
  <Box
    sx={{
      position: "absolute",
      left: `${box.nx * 100}%`,
      top: `${box.ny * 100}%`,
      width: `${box.nw * 100}%`,
      height: `${box.nh * 100}%`,
      zIndex: box.paintIndex + 1,
      transform: box.rotation ? `rotate(${box.rotation}deg)` : undefined,
      transformOrigin: "center center",
      pointerEvents: "none",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        overflow: box.type === "text" ? "visible" : "hidden",
        backgroundColor: hasPaintedContent(box)
          ? "transparent"
          : `${getOriginColor({
              sourceKind: box.sourceKind,
              type: box.type,
            })}1A`,
      }}
    >
      {box.shape ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: box.shape.fill || "#000",
            borderRadius: `${(Number(box.shape.radius) || 0) * scale}px`,
          }}
        />
      ) : null}
      {box.assetUrl ? (
        <CroppedLayerImage
          src={box.assetUrl}
          crop={box.crop}
          objectFit={objectFitFromBox(box.fit)}
        />
      ) : null}
      {box.type === "text" && String(box.text || "").trim() ? (
        <LayerTextPreview
          box={box}
          isEditing={false}
          scale={scale}
          artboardWidth={width}
          artboardHeight={height}
          onTextCommit={() => {}}
        />
      ) : null}
    </Box>
  </Box>
);

/**
 * Up-to-date thumbnail of a template: rebuilds the current layout (layers,
 * text, shapes) from the stored doc and falls back to the saved image when
 * the template has no layers or the doc cannot be loaded.
 */
const TemplateLayoutPreview = ({
  card,
  fallbackUrl = "",
  sx = {},
  fallbackSize = "cover",
  fallbackPosition = "center",
}) => {
  const dispatch = useDispatchApp();
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [doc, setDoc] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const templateID = card?.id || "";
  const version = card?.updateTime || card?.updatedAt || 0;
  const storedPreviewUrl = card?.previewUrl || "";
  const imageUrl = storedPreviewUrl || fallbackUrl || card?.thumbnailUrl || "";
  const [resolvedKey, setResolvedKey] = useState("");
  const isLoading =
    !storedPreviewUrl &&
    Boolean(templateID) &&
    resolvedKey !== getDocCacheKey({ templateID, version });

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
    } else {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        { rootMargin: "200px" }
      );
      observer.observe(node);
      return () => observer.disconnect();
    }
    return undefined;
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (storedPreviewUrl || !isVisible || !templateID) return undefined;
    let cancelled = false;
    loadTemplateDoc({ dispatch, templateID, version }).then((loaded) => {
      if (cancelled) return;
      setDoc(loaded);
      setResolvedKey(getDocCacheKey({ templateID, version }));
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch, isVisible, storedPreviewUrl, templateID, version]);

  const preview = useMemo(
    () => (storedPreviewUrl ? null : buildTemplateLayoutPreview({ doc })),
    [doc, storedPreviewUrl]
  );
  useTemplateFonts({ enabled: Boolean(preview) });

  const containerHeight = containerWidth;
  const scale = preview
    ? Math.min(containerWidth / preview.width, containerHeight / preview.height)
    : 1;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "1",
        overflow: "hidden",
        backgroundColor: "#EEE",
        ...(preview || isLoading
          ? {}
          : {
              backgroundImage: imageUrl ? `url(${imageUrl})` : "none",
              backgroundSize: storedPreviewUrl ? "contain" : fallbackSize,
              backgroundRepeat: "no-repeat",
              backgroundPosition: storedPreviewUrl
                ? "center"
                : fallbackPosition,
            }),
        ...sx,
      }}
    >
      {isLoading ? (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        />
      ) : null}
      {preview && containerWidth > 0 ? (
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: preview.width * scale,
            height: preview.height * scale,
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            overflow: "hidden",
          }}
        >
          {preview.boxes.map((box) => (
            <PreviewBox
              key={box.id}
              box={box}
              width={preview.width}
              height={preview.height}
              scale={scale}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default TemplateLayoutPreview;
